import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmployeeAvatar,
  EmptyState,
  IconBtn,
  PageHeader,
  Spinner,
} from "../../design-system";
import {
  getAbsences,
  getEmployees,
  getHolidayGroups,
  getHolidaysByGroup,
} from "../../helpers/kronaly_helper";
import {
  ABSENCE_STATUS_LABEL,
  ABSENCE_STATUS_TONE,
  ABSENCE_TYPE_LABEL,
  ABSENCE_TYPE_TONE,
  formatDays,
  formatRange,
} from "../../Components/Kronaly/Ausencias/absenceHelpers";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const fullName = (e) => {
  if (!e) return "—";
  const n = `${e.first_name || ""} ${e.last_name || ""}`.trim();
  return n || "(sin nombre)";
};

const ymd = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const todayYmd = () => {
  const d = new Date();
  return ymd(d.getFullYear(), d.getMonth(), d.getDate());
};
const daysInMonth = (year, monthIdx) => new Date(year, monthIdx + 1, 0).getDate();

// js dow: 0=domingo. Devolvemos 0=lunes para simplificar el calendario.
const dowMonFirst = (year, monthIdx, day) => {
  const d = new Date(year, monthIdx, day).getDay();
  return d === 0 ? 6 : d - 1;
};

function Calendario() {
  document.title = "Calendario | Kronaly";

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIdx, setMonthIdx] = useState(today.getMonth());
  const [view, setView] = useState("equipo");

  const [employees, setEmployees] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const monthStart = ymd(year, monthIdx, 1);
  const monthEnd = ymd(year, monthIdx, daysInMonth(year, monthIdx));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Cada call lleva su propio catch para que un fallo aislado no bloquee al resto.
      const [empRes, absRes, hgRes] = await Promise.all([
        getEmployees().catch((e) => ({ __error: e })),
        getAbsences().catch((e) => ({ __error: e })),
        getHolidayGroups().catch((e) => ({ __error: e })),
      ]);

      const errs = [];
      if (empRes?.__error) errs.push(`empleados: ${empRes.__error?.message || empRes.__error}`);
      if (absRes?.__error) errs.push(`ausencias: ${absRes.__error?.message || absRes.__error}`);
      if (hgRes?.__error) errs.push(`festivos: ${hgRes.__error?.message || hgRes.__error}`);

      const emps = !empRes?.__error ? (unwrap(empRes) || []) : [];
      const abs = !absRes?.__error ? (unwrap(absRes) || []) : [];
      const groups = !hgRes?.__error ? (unwrap(hgRes) || []) : [];

      setEmployees(Array.isArray(emps) ? emps : []);
      setAbsences(Array.isArray(abs) ? abs : []);

      const groupIds = (Array.isArray(groups) ? groups : []).map((g) => g.id).filter(Boolean);
      const allHolidays = await Promise.all(
        groupIds.map((id) => getHolidaysByGroup(id).catch(() => null)),
      );
      const map = {};
      allHolidays.forEach((res) => {
        const list = unwrap(res) || [];
        (Array.isArray(list) ? list : []).forEach((h) => {
          if (h?.date) {
            if (map[h.date]) {
              if (!map[h.date].includes(h.name)) map[h.date] += ` · ${h.name}`;
            } else {
              map[h.date] = h.name;
            }
          }
        });
      });
      setHolidaysByDate(map);

      if (errs.length > 0) {
        setError(`Algunos datos no se pudieron cargar — ${errs.join(" · ")}`);
      }
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los datos del calendario.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Filtros del mes
  const monthAbsences = useMemo(() => {
    return absences.filter((a) => {
      if (a.status === "rejected" || a.status === "cancelled") return false;
      // Solapa con el rango del mes
      return a.start_date <= monthEnd && a.end_date >= monthStart;
    });
  }, [absences, monthStart, monthEnd]);

  const upcomingAbsences = useMemo(() => {
    const todayStr = todayYmd();
    return absences
      .filter((a) => a.status === "approved" || a.status === "pending")
      .filter((a) => a.end_date >= todayStr)
      .sort((a, b) => a.start_date.localeCompare(b.start_date))
      .slice(0, 6);
  }, [absences]);

  const monthSummary = useMemo(() => {
    const all = monthAbsences;
    const byStatus = { approved: 0, pending: 0 };
    const byType = {};
    all.forEach((a) => {
      byStatus[a.status] = (byStatus[a.status] || 0) + 1;
      byType[a.type] = (byType[a.type] || 0) + 1;
    });
    return { total: all.length, byStatus, byType };
  }, [monthAbsences]);

  const goPrev = () => {
    if (monthIdx === 0) {
      setYear((y) => y - 1);
      setMonthIdx(11);
    } else {
      setMonthIdx((m) => m - 1);
    }
  };
  const goNext = () => {
    if (monthIdx === 11) {
      setYear((y) => y + 1);
      setMonthIdx(0);
    } else {
      setMonthIdx((m) => m + 1);
    }
  };
  const goToday = () => {
    const d = new Date();
    setYear(d.getFullYear());
    setMonthIdx(d.getMonth());
  };

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow={`${MONTHS_ES[monthIdx]} ${year} · Equipo`}
        title="Calendario"
        description={
          loading
            ? "Cargando…"
            : `${monthSummary.total} ausencias en el mes · ${monthSummary.byStatus.approved || 0} aprobadas · ${monthSummary.byStatus.pending || 0} pendientes`
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <Card padded={false}>
        <CalendarToolbar
          monthLabel={`${MONTHS_ES[monthIdx]} ${year}`}
          view={view}
          setView={setView}
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
        />
        {loading ? (
          <div style={{ padding: 40, textAlign: "center" }}><Spinner /></div>
        ) : view === "equipo" ? (
          <TeamGrid
            year={year}
            monthIdx={monthIdx}
            employees={employees}
            absences={monthAbsences}
            holidaysByDate={holidaysByDate}
          />
        ) : (
          <MonthGrid
            year={year}
            monthIdx={monthIdx}
            employees={employees}
            absences={monthAbsences}
            holidaysByDate={holidaysByDate}
          />
        )}
        <Legend />
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <SummaryCard summary={monthSummary} monthLabel={MONTHS_ES[monthIdx]} />
        <UpcomingCard items={upcomingAbsences} employeesById={Object.fromEntries(employees.map((e) => [e.id, e]))} />
      </div>
    </div>
  );
}

function CalendarToolbar({ monthLabel, view, setView, onPrev, onNext, onToday }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "12px 16px",
        borderBottom: "1px solid var(--kr-border-subtle)",
        gap: 10,
      }}
    >
      <Button variant="secondary" size="sm" onClick={onToday}>Hoy</Button>
      <IconBtn icon="ChevronLeft" onClick={onPrev} />
      <IconBtn icon="ChevronRight" onClick={onNext} />
      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)", marginLeft: 4, textTransform: "capitalize" }}>
        {monthLabel}
      </span>
      <div style={{ flex: 1 }} />
      <SegmentedControl
        value={view}
        onChange={setView}
        options={[{ id: "equipo", label: "Equipo" }, { id: "mes", label: "Mes" }]}
      />
    </div>
  );
}

function SegmentedControl({ value, onChange, options }) {
  return (
    <div style={{ display: "inline-flex", padding: 2, background: "var(--kr-neutral-100)", borderRadius: 6 }}>
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          style={{
            border: "none", cursor: "pointer",
            padding: "5px 12px", fontSize: 12, fontWeight: 600,
            background: value === o.id ? "#fff" : "transparent",
            color: value === o.id ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
            borderRadius: 4,
            boxShadow: value === o.id ? "var(--kr-shadow-xs)" : "none",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TeamGrid({ year, monthIdx, employees, absences, holidaysByDate }) {
  const dim = daysInMonth(year, monthIdx);
  const days = Array.from({ length: dim }, (_, i) => i + 1);
  const todayStr = todayYmd();
  const dayName = ["L", "M", "X", "J", "V", "S", "D"];
  const cellW = 28;

  if (employees.length === 0) {
    return (
      <EmptyState
        icon="Users"
        title="Sin empleados"
        description="Crea empleados en Equipo › Empleados para ver el calendario."
      />
    );
  }

  const totalWidth = 240 + cellW * dim;

  return (
    <div style={{ overflow: "auto" }}>
      <div style={{ minWidth: totalWidth }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `240px repeat(${dim}, ${cellW}px)`,
            alignItems: "stretch",
            borderBottom: "1px solid var(--kr-border)",
            background: "var(--kr-neutral-50)",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <div style={{ padding: "10px 16px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
            Empleado · {employees.length}
          </div>
          {days.map((d) => {
            const dateStr = ymd(year, monthIdx, d);
            const dow = dowMonFirst(year, monthIdx, d);
            const isWeekend = dow >= 5;
            const isToday = dateStr === todayStr;
            const isHoliday = Boolean(holidaysByDate[dateStr]);
            return (
              <div
                key={d}
                title={isHoliday ? holidaysByDate[dateStr] : undefined}
                style={{
                  padding: "6px 0",
                  textAlign: "center",
                  background: isToday
                    ? "var(--kr-action-soft-bg)"
                    : isWeekend
                      ? "var(--kr-neutral-100)"
                      : "transparent",
                  borderLeft: "1px solid var(--kr-border-subtle)",
                  color: isToday ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
                }}
              >
                <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em" }}>{dayName[dow]}</div>
                <div
                  style={{
                    fontSize: 11,
                    fontFamily: "var(--kr-font-mono)",
                    fontWeight: isToday ? 700 : 500,
                    color: isToday
                      ? "var(--kr-action-soft-fg)"
                      : isHoliday
                        ? "var(--kr-danger-700)"
                        : "var(--kr-fg-strong)",
                  }}
                >
                  {d}
                </div>
              </div>
            );
          })}
        </div>

        {employees.map((p, idx) => {
          const personAbs = absences.filter((a) => a.employee_profile_id === p.id);
          return (
            <div
              key={p.id}
              style={{
                display: "grid",
                gridTemplateColumns: `240px repeat(${dim}, ${cellW}px)`,
                alignItems: "stretch",
                borderBottom: idx === employees.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
                position: "relative",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px" }}>
                <EmployeeAvatar employeeId={p.id} hasAvatar={true} name={fullName(p)} size={26} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {fullName(p)}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--kr-fg-muted)" }}>{p.position || "—"}</div>
                </div>
              </div>
              {/* Background cells */}
              {days.map((d) => {
                const dateStr = ymd(year, monthIdx, d);
                const dow = dowMonFirst(year, monthIdx, d);
                const isWeekend = dow >= 5;
                const isToday = dateStr === todayStr;
                const isHoliday = Boolean(holidaysByDate[dateStr]);
                return (
                  <div
                    key={d}
                    style={{
                      borderLeft: "1px solid var(--kr-border-subtle)",
                      background: isToday
                        ? "var(--kr-action-soft-bg)"
                        : isHoliday
                          ? "var(--kr-status-danger-bg)"
                          : isWeekend
                            ? "var(--kr-neutral-100)"
                            : "transparent",
                    }}
                  />
                );
              })}
              {/* Absence bars */}
              {personAbs.map((a) => {
                // Recortamos el rango al mes visible
                const startDay = Math.max(1, dayOfMonth(a.start_date, year, monthIdx, true));
                const endDay = Math.min(dim, dayOfMonth(a.end_date, year, monthIdx, false));
                if (startDay > dim || endDay < 1 || endDay < startDay) return null;
                const left = 240 + (startDay - 1) * cellW + 2;
                const width = (endDay - startDay + 1) * cellW - 4;
                const isApproved = a.status === "approved";
                const isPending = a.status === "pending";
                const bg = isApproved ? "var(--kr-success-500)" : "#facc15";
                const fg = isApproved ? "#fff" : "#713f12";
                return (
                  <div
                    key={a.id}
                    title={`${ABSENCE_TYPE_LABEL[a.type]} · ${formatRange(a.start_date, a.end_date)}${isPending ? " (pendiente)" : ""}`}
                    style={{
                      position: "absolute",
                      left,
                      top: 8,
                      height: "calc(100% - 16px)",
                      width,
                      background: bg,
                      color: fg,
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: 6,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      opacity: isPending ? 0.95 : 1,
                    }}
                  >
                    {width > 36 && (ABSENCE_TYPE_LABEL[a.type] || a.type)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthGrid({ year, monthIdx, employees, absences, holidaysByDate }) {
  const employeesById = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, e])),
    [employees],
  );
  const dim = daysInMonth(year, monthIdx);
  const startDow = dowMonFirst(year, monthIdx, 1);
  const todayStr = todayYmd();

  const cells = [];
  // Padding inicial (días del mes anterior)
  const prevDim = daysInMonth(year, monthIdx - 1);
  for (let i = 0; i < startDow; i++) {
    cells.push({ day: prevDim - startDow + i + 1, prev: true });
  }
  for (let d = 1; d <= dim; d++) cells.push({ day: d });
  while (cells.length % 7 !== 0) {
    cells.push({ day: cells[cells.length - 1].day + 1, next: true });
  }

  const dayNames = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid var(--kr-border-subtle)", marginBottom: 4 }}>
        {dayNames.map((n) => (
          <div key={n} style={{ padding: "8px 12px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
            {n}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((c, i) => {
          const dim2 = c.prev || c.next;
          const dateStr = dim2 ? null : ymd(year, monthIdx, c.day);
          const isToday = !dim2 && dateStr === todayStr;
          const isHoliday = !dim2 && Boolean(holidaysByDate[dateStr]);
          const dayAbs = dim2
            ? []
            : absences.filter((a) => a.start_date <= dateStr && a.end_date >= dateStr);
          return (
            <div
              key={i}
              style={{
                minHeight: 96,
                padding: 8,
                border: `1px solid ${isToday ? "var(--kr-action)" : "var(--kr-border-subtle)"}`,
                borderRadius: 8,
                background: dim2 ? "var(--kr-neutral-50)" : "#fff",
                opacity: dim2 ? 0.5 : 1,
                position: "relative",
              }}
              title={isHoliday ? holidaysByDate[dateStr] : undefined}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span
                  style={{
                    fontFamily: "var(--kr-font-mono)",
                    fontSize: 12,
                    fontWeight: isToday ? 700 : 500,
                    color: isToday
                      ? "var(--kr-action-soft-fg)"
                      : isHoliday
                        ? "var(--kr-danger-700)"
                        : "var(--kr-fg-strong)",
                  }}
                >
                  {c.day}
                </span>
                {isHoliday && <Badge tone="danger">Festivo</Badge>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {dayAbs.slice(0, 3).map((a) => {
                  const emp = employeesById[a.employee_profile_id];
                  const isApproved = a.status === "approved";
                  return (
                    <div
                      key={a.id}
                      title={`${ABSENCE_TYPE_LABEL[a.type]} · ${fullName(emp)}${a.status === "pending" ? " (pendiente)" : ""}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "1px 5px",
                        borderRadius: 3,
                        background: isApproved ? "rgba(34, 197, 94, 0.15)" : "rgba(250, 204, 21, 0.25)",
                        color: isApproved ? "var(--kr-success-700)" : "#713f12",
                        fontSize: 10,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          background: isApproved ? "var(--kr-success-500)" : "#facc15",
                          borderRadius: 999,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {(emp?.first_name || a.employee_profile_id || "").split(" ")[0]}
                      </span>
                    </div>
                  );
                })}
                {dayAbs.length > 3 && (
                  <span style={{ fontSize: 10, color: "var(--kr-fg-muted)", paddingLeft: 5 }}>
                    +{dayAbs.length - 3} más
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 14,
        padding: "12px 16px",
        borderTop: "1px solid var(--kr-border-subtle)",
        fontSize: 11,
        color: "var(--kr-fg-muted)",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--kr-success-500)" }} />
        Aprobada
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: "#facc15" }} />
        Pendiente
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--kr-danger-500)" }} />
        Festivo
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--kr-action-soft-bg)", border: "1px solid var(--kr-action)" }} />
        Hoy
      </span>
    </div>
  );
}

function SummaryCard({ summary, monthLabel }) {
  const types = Object.entries(summary.byType);
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
            Resumen del mes
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: "var(--kr-fg-strong)", marginTop: 4, textTransform: "capitalize" }}>
            {monthLabel}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
        <Tile label="Total" value={summary.total} tone="default" />
        <Tile label="Aprobadas" value={summary.byStatus.approved || 0} tone="success" />
        <Tile label="Pendientes" value={summary.byStatus.pending || 0} tone="warning" />
      </div>

      <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginBottom: 6 }}>Por tipo</div>
      {types.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", padding: "4px 0" }}>
          Sin ausencias en el mes.
        </div>
      ) : (
        types.map(([type, count]) => (
          <div
            key={type}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 0",
              borderBottom: "1px solid var(--kr-border-subtle)",
              fontSize: 13,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Badge tone={ABSENCE_TYPE_TONE[type] || "neutral"}>
                {ABSENCE_TYPE_LABEL[type] || type}
              </Badge>
            </span>
            <span style={{ fontFamily: "var(--kr-font-mono)", fontWeight: 600, color: "var(--kr-fg-strong)" }}>
              {count}
            </span>
          </div>
        ))
      )}
    </Card>
  );
}

const TILE_TONE_COLOR = {
  default: "var(--kr-fg-strong)",
  success: "var(--kr-success-700)",
  warning: "#a16207",
};

function Tile({ label, value, tone = "default" }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--kr-font-mono)",
          fontSize: 22,
          fontWeight: 700,
          color: TILE_TONE_COLOR[tone] || TILE_TONE_COLOR.default,
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function UpcomingCard({ items, employeesById }) {
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
            Próximas ausencias
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: "var(--kr-fg-strong)", marginTop: 4 }}>
            Aprobadas y pendientes
          </div>
        </div>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", padding: "4px 0" }}>
          Sin ausencias próximas.
        </div>
      ) : (
        items.map((a, i) => {
          const emp = employeesById[a.employee_profile_id];
          return (
            <div
              key={a.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 0",
                borderBottom: i < items.length - 1 ? "1px solid var(--kr-border-subtle)" : "none",
              }}
            >
              <EmployeeAvatar employeeId={a.employee_profile_id} hasAvatar={true} name={fullName(emp)} size={28} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "var(--kr-fg-strong)", fontSize: 13, fontWeight: 500 }}>
                  {fullName(emp)}
                </div>
                <div style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>
                  {ABSENCE_TYPE_LABEL[a.type] || a.type} · {formatDays(a.days_count)}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)" }}>
                  {formatRange(a.start_date, a.end_date)}
                </span>
                <Badge tone={ABSENCE_STATUS_TONE[a.status]} dot>
                  {ABSENCE_STATUS_LABEL[a.status]}
                </Badge>
              </div>
            </div>
          );
        })
      )}
    </Card>
  );
}

/**
 * Convierte una fecha YYYY-MM-DD al día del mes (1..N) si cae en el mes visible;
 * si la fecha es anterior al mes visible devuelve 0/negativo (clipping abajo);
 * si es posterior, devuelve dim+algo (clipping arriba).
 *   forStart=true → si la fecha es antes del mes visible, devolvemos 1 (start clip).
 *   forStart=false → si la fecha es después del mes visible, devolvemos dim (end clip).
 */
function dayOfMonth(dateStr, year, monthIdx, forStart) {
  if (!dateStr) return forStart ? 1 : daysInMonth(year, monthIdx);
  const [y, m, d] = dateStr.split("-").map(Number);
  if (y < year || (y === year && m - 1 < monthIdx)) {
    return forStart ? 1 : 0;
  }
  if (y > year || (y === year && m - 1 > monthIdx)) {
    return forStart ? daysInMonth(year, monthIdx) + 1 : daysInMonth(year, monthIdx);
  }
  return d;
}

export default Calendario;
